import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, fieldErrors, send } from '../lib/api';
import { focusFirstError, readForm, textField } from '../lib/form';
import { icon } from '../lib/icons';
import { LightElement } from '../lib/light-element';
import './hl-password-field';

/** Sign-in form (spec §5.1). Posts to /api/account/login and follows the returned redirect. */
@customElement('hl-login-form')
export class HlLoginForm extends LightElement {
  @property({ attribute: 'return-url' }) returnUrl = '';

  /** Navigation hook; replaced in tests. */
  @property({ attribute: false }) navigate: (url: string) => void = (url) => window.location.assign(url);

  @state() private busy = false;

  @state() private error = '';

  @state() private errors: Record<string, string> = {};

  private async onSubmit(e: SubmitEvent) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const values = readForm(form, ['rememberMe']);

    const errors: Record<string, string> = {};
    if (!String(values.userName ?? '').trim()) errors.userName = 'Enter your username or email.';
    if (!values.password) errors.password = 'Enter your password.';
    this.errors = errors;
    this.error = '';
    if (Object.keys(errors).length > 0) {
      await this.updateComplete;
      focusFirstError(this, errors);
      return;
    }

    this.busy = true;
    const result = await send(() =>
      api().POST('/api/account/login', {
        body: {
          userName: String(values.userName).trim(),
          password: String(values.password),
          rememberMe: values.rememberMe === true,
          returnUrl: this.returnUrl || null,
        },
      }));
    if (result.ok) {
      this.navigate(result.data.redirectUrl);
      return;
    }

    this.busy = false;
    this.errors = fieldErrors(result.problem);
    this.error = Object.keys(this.errors).length ? '' : (result.problem.title ?? '');
    await this.updateComplete;
    const password = this.querySelector<HTMLInputElement>('input[name="password"]');
    if (password && !this.errors.userName) {
      password.value = '';
      password.focus();
    }
  }

  override render() {
    return html`
      <form class="hl-form" novalidate @submit=${this.onSubmit}>
        <p class="hl-error hl-callout-error" role="alert" data-testid="login-error">${this.error ? html`${icon('alert', 18)}${this.error}` : nothing}</p>
        ${textField({ id: 'login-username', name: 'userName', label: 'Username or email', autocomplete: 'username', error: this.errors.userName })}
        <hl-password-field
          input-id="login-password"
          name="password"
          autocomplete="current-password"
          .error=${this.errors.password ?? ''}
        ></hl-password-field>
        <div class="form-check hl-check">
          <input class="form-check-input" type="checkbox" id="login-remember" name="rememberMe" />
          <label class="form-check-label" for="login-remember">Keep me signed in on this device</label>
        </div>
        <button type="submit" class="btn btn-primary hl-btn-block" ?disabled=${this.busy} data-testid="sign-in">
          ${this.busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hl-login-form': HlLoginForm;
  }
}
